import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { format, formatDistanceToNow } from "date-fns";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import {
  useListEngagementEvents,
  type EngagementEvent,
  type ListEngagementEventsType,
} from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Loader2,
  MapPin,
  Eye,
  MousePointerClick,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Download,
  CalendarIcon,
} from "lucide-react";

const PAGE_SIZE = 50;

function buildCsvUrl(
  type: string,
  hideBots: boolean,
  dateRange: DateRange | undefined,
): string {
  const params = new URLSearchParams();
  if (type !== "all") params.set("type", type);
  if (!hideBots) params.set("hideBots", "false");
  if (dateRange?.from) params.set("startDate", format(dateRange.from, "yyyy-MM-dd"));
  if (dateRange?.to) params.set("endDate", format(dateRange.to, "yyyy-MM-dd"));
  const qs = params.toString();
  return `/api/engagement.csv${qs ? `?${qs}` : ""}`;
}

function formatDateRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) return "Last 90 days";
  const from = format(range.from, "MMM d, yyyy");
  if (!range.to) return `From ${from}`;
  const to = format(range.to, "MMM d, yyyy");
  return `${from} – ${to}`;
}

/**
 * Map an ISO-3166 alpha-2 country code (e.g. "US") to its display
 * name. We use the platform Intl.DisplayNames API so we never have
 * to ship our own country list. Falls back to the raw code on any
 * environment that doesn't have DisplayNames or that doesn't
 * recognise the code.
 */
function countryName(code: string | null): string | null {
  if (!code) return null;
  try {
    const dn = new Intl.DisplayNames([navigator.language || "en"], {
      type: "region",
    });
    return dn.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

function formatLocation(event: EngagementEvent): string {
  const country = countryName(event.country);
  if (event.city && country) return `${event.city}, ${country}`;
  if (event.city) return event.city;
  if (country) return country;
  return "—";
}

function truncateUrl(url: string, max = 60): string {
  if (url.length <= max) return url;
  return `${url.slice(0, max - 1)}…`;
}

/**
 * Engagement page — per-event breakdown of every recorded open and
 * click on the user's recent sends. Mirrors the visual style of
 * `/app/logs` so the two pages feel like siblings: same Card
 * container, same Badge primitives, same `formatDistanceToNow`-style
 * relative timestamps with absolute on hover.
 */
export default function Engagement() {
  const [type, setType] = useState<ListEngagementEventsType>("all");
  const [hideBots, setHideBots] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const csvAbortRef = useRef<AbortController | null>(null);

  const cancelPendingExport = useCallback(() => {
    if (csvAbortRef.current) {
      csvAbortRef.current.abort();
      csvAbortRef.current = null;
    }
  }, []);

  const handleDownloadCsv = useCallback(async () => {
    cancelPendingExport();
    const controller = new AbortController();
    csvAbortRef.current = controller;
    setIsExporting(true);
    try {
      const res = await fetch(buildCsvUrl(type, hideBots, dateRange), {
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`Server responded with ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "engagement-events.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV export downloaded");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      toast.error("CSV export failed — please try again");
    } finally {
      if (csvAbortRef.current === controller) {
        csvAbortRef.current = null;
        setIsExporting(false);
      }
    }
  }, [type, hideBots, dateRange, cancelPendingExport]);
  // We keep a stack of cursors so the "Previous" button can walk
  // backwards through pages without having to refetch from the start.
  // Each entry is the cursor that was passed to fetch that page;
  // `undefined` means "the first page (no cursor)".
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([
    undefined,
  ]);
  const currentCursor = cursorStack[cursorStack.length - 1];

  const queryParams = useMemo(
    () => ({
      type,
      hideBots,
      limit: PAGE_SIZE,
      ...(currentCursor ? { cursor: currentCursor } : {}),
      ...(dateRange?.from ? { startDate: format(dateRange.from, "yyyy-MM-dd") } : {}),
      ...(dateRange?.to ? { endDate: format(dateRange.to, "yyyy-MM-dd") } : {}),
    }),
    [type, hideBots, currentCursor, dateRange],
  );

  const { data, isLoading, isFetching, error } =
    useListEngagementEvents(queryParams);

  // Whenever the user changes the filters we reset back to page 1 so
  // the cursor we cached for a different filter set can't show stale
  // results.
  const cancelExportOnFilterChange = useCallback(() => {
    if (csvAbortRef.current) {
      csvAbortRef.current.abort();
      csvAbortRef.current = null;
      setIsExporting(false);
      toast.info("CSV export cancelled — filters changed");
    }
  }, []);

  const onTypeChange = (next: string) => {
    cancelExportOnFilterChange();
    setCursorStack([undefined]);
    setType(next as ListEngagementEventsType);
  };
  const onHideBotsChange = (next: boolean) => {
    cancelExportOnFilterChange();
    setCursorStack([undefined]);
    setHideBots(next);
  };
  const onDateRangeChange = (next: DateRange | undefined) => {
    const isComplete = !next || (next.from && next.to);
    if (isComplete) {
      cancelExportOnFilterChange();
      setCursorStack([undefined]);
    }
    setDateRange(next);
  };

  const events = data?.events ?? [];
  const lookbackDays = data?.lookbackDays ?? 90;
  const pageNumber = cursorStack.length;
  const hasNext = !!data?.nextCursor;
  const hasPrevious = cursorStack.length > 1;

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MapPin className="w-6 h-6 text-primary" />
              Engagement
            </h1>
            <p className="text-muted-foreground">
              Every recorded open and click on the emails you've sent —
              with IP, time, and approximate location.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="engagement-date-range-trigger"
                >
                  <CalendarIcon className="w-4 h-4 mr-1.5" />
                  {formatDateRangeLabel(dateRange)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={onDateRangeChange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  defaultMonth={
                    dateRange?.from
                      ? dateRange.from
                      : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                  }
                  data-testid="engagement-date-range-calendar"
                />
                {dateRange?.from && (
                  <div className="border-t px-3 py-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDateRangeChange(undefined)}
                      data-testid="engagement-date-range-reset"
                    >
                      Reset to last 90 days
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              size="sm"
              disabled={isExporting}
              onClick={handleDownloadCsv}
              data-testid="engagement-download-csv"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              {isExporting ? "Exporting…" : "Download CSV"}
            </Button>
            <div className="flex items-center gap-2">
              <Switch
                id="engagement-hide-bots"
                checked={hideBots}
                onCheckedChange={onHideBotsChange}
                data-testid="engagement-hide-bots-toggle"
              />
              <Label
                htmlFor="engagement-hide-bots"
                className="text-sm cursor-pointer select-none"
              >
                Hide bots
              </Label>
            </div>
            <div className="w-[180px]">
              <Select value={type} onValueChange={onTypeChange}>
                <SelectTrigger data-testid="engagement-type-filter">
                  <SelectValue placeholder="Filter type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  <SelectItem value="open">Opens only</SelectItem>
                  <SelectItem value="click">Clicks only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div
            className="text-center py-20 px-4 border-2 border-dashed border-destructive/40 rounded-xl bg-destructive/5"
            data-testid="engagement-error-state"
          >
            <p className="text-destructive font-medium">
              Couldn't load engagement events.
            </p>
            <p className="text-muted-foreground text-sm mt-2">
              Try refreshing the page.
            </p>
          </div>
        ) : events.length === 0 ? (
          <div
            className="text-center py-20 px-4 border-2 border-dashed border-border rounded-xl bg-card"
            data-testid="engagement-empty-state"
          >
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">
              No engagement events yet
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {dateRange?.from ? (
                <>
                  {dateRange.to
                    ? `No events between ${format(dateRange.from, "MMM d, yyyy")} – ${format(dateRange.to, "MMM d, yyyy")}.`
                    : `No events since ${format(dateRange.from, "MMM d, yyyy")}.`}
                </>
              ) : (
                <>
                  They'll appear here when recipients open your emails or
                  click links. Showing the last {lookbackDays} days.
                </>
              )}
            </p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid="engagement-table"
                >
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium">Recipient</th>
                      <th className="px-4 py-3 font-medium">Subject</th>
                      <th className="px-4 py-3 font-medium">IP</th>
                      <th className="px-4 py-3 font-medium">Location</th>
                      <th className="px-4 py-3 font-medium">Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {events.map((ev) => (
                      <EventRow key={ev.id} event={ev} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TooltipProvider>
        )}

        {events.length > 0 && (
          <div
            className="flex items-center justify-between text-sm"
            data-testid="engagement-pagination"
          >
            <div className="text-muted-foreground flex items-center gap-2">
              Page {pageNumber}
              {isFetching && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasPrevious || isFetching}
                onClick={() => setCursorStack((s) => s.slice(0, -1))}
                data-testid="engagement-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasNext || isFetching}
                onClick={() =>
                  setCursorStack((s) => [...s, data?.nextCursor ?? undefined])
                }
                data-testid="engagement-next-page"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: EngagementEvent }) {
  const occurred = new Date(event.occurredAt);
  const relative = formatDistanceToNow(occurred, { addSuffix: true });
  const absolute = occurred.toLocaleString();
  const location = formatLocation(event);
  const isClick = event.type === "click";

  return (
    <tr
      className="hover:bg-muted/30 transition-colors"
      data-testid={`engagement-row-${event.id}`}
      data-event-type={event.type}
    >
      <td className="px-4 py-3 whitespace-nowrap">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="text-muted-foreground cursor-help"
              data-testid={`engagement-row-${event.id}-when`}
            >
              {relative}
            </span>
          </TooltipTrigger>
          <TooltipContent>{absolute}</TooltipContent>
        </Tooltip>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {isClick ? (
            <Badge
              variant="outline"
              className="bg-blue-500/10 text-blue-600 border-blue-500/20"
            >
              <MousePointerClick className="w-3 h-3 mr-1" />
              Click
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
            >
              <Eye className="w-3 h-3 mr-1" />
              Open
            </Badge>
          )}
          {event.isLikelyBot && (
            <Badge
              variant="outline"
              className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
              title="Likely automated prefetcher (Apple MPP, GoogleImageProxy, SafeLinks, …)"
              data-testid={`engagement-row-${event.id}-bot-badge`}
            >
              Bot
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 max-w-[220px] truncate" title={event.to}>
        {event.to}
      </td>
      <td className="px-4 py-3 max-w-[260px]">
        <Link
          href={`/app/logs/${event.emailLogId}`}
          className="text-primary hover:underline truncate block"
          title={event.subject}
          data-testid={`engagement-row-${event.id}-subject`}
        >
          {event.subject}
        </Link>
      </td>
      <td
        className="px-4 py-3 font-mono text-xs whitespace-nowrap text-muted-foreground"
        data-testid={`engagement-row-${event.id}-ip`}
      >
        {event.ip ?? "—"}
      </td>
      <td
        className="px-4 py-3 whitespace-nowrap"
        data-testid={`engagement-row-${event.id}-location`}
      >
        {location}
      </td>
      <td className="px-4 py-3 max-w-[260px]">
        {event.url ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline truncate max-w-full font-mono text-xs"
                data-testid={`engagement-row-${event.id}-url`}
              >
                <span className="truncate">{truncateUrl(event.url)}</span>
                <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            </TooltipTrigger>
            <TooltipContent className="max-w-md break-all">
              {event.url}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}
